// Tells the tree pane that something else changed the tree.
//
// The mutation routes are the same three the UI uses, so a split written by an
// agent lands in the model exactly as a split written by a person does. The
// difference is that the browser has no idea it happened: the write came from the
// server, not from a click, and the store's own reload never ran.
//
// The chat stream already carries server-owned activity keys and the summary
// panel already refreshes itself off one (`summary_refresh`). This is the same
// mechanism for the tree, kept as a small bus rather than a prop chain because
// the emitter is the chat panel and the listener is the tree pane, and neither is
// an ancestor of the other.
//
// Coalescing is the reason this is not just a callback. An agent building a tree
// writes a run of splits, and each one emits. Refetching the whole definition
// once per split would strobe the pane and queue requests behind each other, so
// emits inside one window collapse into a single refresh per tree.

/** How long to wait for more emits before refreshing. */
const WINDOW_MS = 250;

/** `null` means the emitter did not say which tree, so refresh whatever is open. */
export type TreeRefreshTarget = number | null;

export type TreeRefreshHandler = (treeId: TreeRefreshTarget) => void;

/**
 * One thing an agent did to a tree.
 *
 * `label` is the server's own words. The client does not compose this sentence
 * and must not parse it: the agent owns what it says it did, and reading a node
 * id back out of prose is the kind of join that works until someone rewords a
 * message.
 */
export interface TreeStep {
  at: number;
  label: string;
  treeId: TreeRefreshTarget;
}

export type TreeStepHandler = (step: TreeStep) => void;

type Schedule = (run: () => void, delayMs: number) => void;

export function createTreeRefreshBus(
  schedule: Schedule = (run, delayMs) => {
    setTimeout(run, delayMs);
  },
  windowMs = WINDOW_MS
) {
  const handlers = new Set<TreeRefreshHandler>();
  // Steps are delivered immediately and never coalesced. The refresh channel
  // exists to avoid twenty refetches; the log exists to show twenty steps, so
  // collapsing them there would delete the thing being watched.
  const stepHandlers = new Set<TreeStepHandler>();
  // Ids seen since the last flush. A Set, so ten splits on tree 2 are one
  // refresh, while splits on two different trees are one refresh each.
  let pending = new Set<TreeRefreshTarget>();
  let scheduled = false;

  const flush = () => {
    scheduled = false;
    const targets = pending;
    pending = new Set();
    // A handler added or removed during the flush must not change this pass, and
    // a throwing handler must not stop the others: one broken listener should not
    // leave every other pane stale.
    [...handlers].forEach((handler) => {
      targets.forEach((target) => {
        try {
          handler(target);
        } catch {
          // ignored on purpose, see above
        }
      });
    });
  };

  return {
    subscribe(handler: TreeRefreshHandler): () => void {
      handlers.add(handler);
      return () => {
        handlers.delete(handler);
      };
    },

    subscribeSteps(handler: TreeStepHandler): () => void {
      stepHandlers.add(handler);
      return () => {
        stepHandlers.delete(handler);
      };
    },

    /**
     * One agent step: announced now, and folded into the next refresh.
     *
     * Both from one call, because a step and the refetch it implies are the same
     * event seen at two rates. Two emit sites would let a listener and the canvas
     * disagree about what happened.
     *
     * The step channel currently has no subscriber: the Agent activity card that
     * read it was removed, since the chat panel already narrates the same steps
     * beside it. Do NOT delete this method or the `emitTreeStep` call on that
     * basis. `this.emit` below is the ONLY thing that refetches the tree after an
     * agent writes to it, and `emitTreeRefresh` is called from nowhere.
     */
    emitStep(step: TreeStep) {
      [...stepHandlers].forEach((handler) => {
        try {
          handler(step);
        } catch {
          // one broken listener must not stop the rest, as below
        }
      });
      this.emit(step.treeId);
    },

    emit(treeId: TreeRefreshTarget) {
      pending.add(treeId);
      if (scheduled) return;
      scheduled = true;
      schedule(flush, windowMs);
    },

    /** Test seam. Runs whatever is pending now instead of waiting. */
    flushNow: flush,

    get size() {
      return handlers.size;
    },
  };
}

const bus = createTreeRefreshBus();

export const onTreeRefresh = bus.subscribe;
export const emitTreeRefresh = bus.emit;
export const onTreeStep = bus.subscribeSteps;
export const emitTreeStep = (step: TreeStep) => bus.emitStep(step);
